<template>
  <div class="theme-teasers">
    <Heading
      class="theme-teasers__heading"
      tag="h2"
      size="md"
      font-weight="regular"
      spaces-bottom-rem
    >
      Themenwelten
    </Heading>
    <div class="theme-teasers__list">
      <template v-for="(teaser, id) in teasersData">
        <MouseOverTeaser
          :key="id"
          class="theme-teaser"
          :background-animation-class="teaser.backgroundAnimationClass"
          :background-style="teaser.backgroundStyle"
          :background-image="teaser.backgroundImage"
          :class="teaser.backgroundClass"
          :link="teaser.link"
        >
          <template #header>
            <CdnImage
              v-if="teaser.header.brand?.logo"
              class="theme-teaser__manufacturer-logo"
              :src="teaser.header.brand.logo"
              :alt="teaser.header.brand.name"
              :sizes="teaser.header.brand.sizes"
              render-without-figure
              :style="`aspect-ratio: ${teaser.header.brand.aspectRatio}; ${teaser.header.brand.styles}`"
              lazy
            />
            <Heading
              class="theme-teaser__manufacturer-heading"
              font-weight="ultra"
              tag="h3"
              size="md"
            >
              {{ teaser.header.heading }}
            </Heading>
          </template>
          <template #image="{ mouseOver }">
            <CdnImage
              v-for="image in teaser.contentImages"
              :key="image.src"
              class="mouseover-teaser__element"
              :style="image.styles"
              :class="{
                [image.animationClass]: mouseOver,
              }"
              :sizes="image.sizes"
              :src="image.src"
              :src-md="image.srcMd"
              :aspect-ratio="image.aspectRatio"
              :aspect-ratio-md="image.aspectRatioMd"
              :alt="image.alt"
              render-without-figure
              lazy
            />
          </template>
        </MouseOverTeaser>
      </template>
    </div>

    <LinkButton
      tag="nuxt-link"
      to="/themenwelten"
      no-styling
      class="theme-teasers__note"
    >
      Weitere Themenwelten entdecken
      <span class="theme-teasers__arrow-symbol"
        ><Arrow
          direction="right"
          tag="span"
      /></span>
    </LinkButton>
  </div>
</template>

<script setup lang="ts">
import MouseOverTeaser from '@/components/mouseover-teaser.vue';
import { Arrow, Heading, LinkButton } from '@i22/td-longtail-vue-components';
import CdnImage from '@/components/cdn-image.vue';
import { addLogo } from '~/lib/brand';

type Teaser = {
  link: string;
  contentImages: {
    src: string;
    sizes?: string;
    srcMd?: string;
    styles?: string | Record<string, any>;
    aspectRatio?: string;
    aspectRatioMd?: string;
    alt?: string;
    animationClass?: string;
  }[];
  header: {
    heading: string;
    brand?: {
      slug?: string;
      sizes?: string;
      styles?: string | Record<string, any>;
      name?: string;
    };
  };
  backgroundClass?: string;
  backgroundAnimationClass?: string;
  backgroundStyle?: string;
  backgroundImage?: {
    alt?: string;
    src: string;
    sizes?: string;
  };
};

const teasersData: Teaser[] = [
  {
    link: '/anker',
    backgroundAnimationClass: 'mouseover-teaser--rounded-rotate-bg',
    header: {
      brand: addLogo({
        name: 'Anker Logo',
        slug: 'anker',
      }),
      heading: 'Die Nr.1 für mobiles Laden',
    },
    backgroundImage: {
      alt: 'Anker Ladegeräte',
      src: '/content/content-images/homepage/theme-teasers/1bf7d247738133107a006c8e0f11778d/anker-themenweltteaser-bg@2x.jpg',
      sizes: 'sm:100vw md:50vw lg:928px',
    },
    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/ed9192f25951f0fc4546dde13348fd57/anker-themenweltteaser-produkte@2x.png',
        alt: 'Anker Ladegeräte',
        aspectRatio: '836 / 675',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:80vw sm:80vw md:50vw lg:464px',
      },
    ],
  },
  {
    link: '/a-good-company',
    backgroundAnimationClass: 'mouseover-teaser--rounded-rotate-bg',
    header: {
      brand: addLogo({
        name: 'agood company Logo',
        slug: 'agood',
      }),
      heading: 'Schütze dein Smartphone und die Umwelt',
    },
    backgroundImage: {
      alt: 'Eine durchsichtige Handyhülle von agood company unter einem Smartphone',
      src: '/content/content-images/homepage/theme-teasers/ec88e22714324dd5e23b84890cce72c7/agood-themenwelt-teaser-bg@2x.jpg',
      sizes: 'sm:100vw md:50vw lg:928px',
    },
    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/70baed837d7482161626ebc31739c99c/agood-case-no-plastic-green-magenta@2x.png',
        alt: 'Handyhülle von agood company',
        aspectRatio: '338 / 539',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:50vw sm:80vw md:50vw lg:464px',
      },
    ],
  },
  {
    link: '/panzerglass',
    backgroundAnimationClass: 'mouseover-teaser--rounded-white',
    backgroundStyle: 'background: linear-gradient(219.01deg, #DAE7EF 0%, #B3C4CE 100%);',
    header: {
      brand: addLogo({
        name: 'PanzerGlass Logo',
        slug: 'panzerglass',
      }),
      heading: 'Die Lösung für dein Displayschutz',
    },
    contentImages: [
      {
        src: '/content/content-images/homepage/theme-teasers/69969af05fd20c73dfa65f813283785c/panzerglass-homepage-teaser.png',
        alt: 'PanzerGlass Produkte',
        aspectRatio: ' 464 / 444',
        styles: 'padding: 12px;',
        animationClass: 'mouseover-teaser--scale',
        sizes: 'xs:80vw sm:80vw md:50vw lg:464px',
      },
    ],
  },
];
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.theme-teasers {
  padding-inline: $grid-base;
  &__heading {
    z-index: 1;
  }

  &__list {
    display: grid;
    gap: $grid-base;
    grid-template-columns: 1fr;
    margin-bottom: 1rem;

    @include mq($mq-sm) {
      grid-template-columns: 1fr 1fr;
      grid-gap: $grid-base * 2;
    }
    @include mq($mq-md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__note {
    color: var(--color-font);
    display: flex;

    &:hover {
      .theme-teasers__arrow-symbol {
        &::before {
          min-width: $grid-base;
        }
      }
    }
  }
  &__arrow-symbol {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: $grid-base;
    gap: 2px;
    bottom: 1px;
    &::before {
      content: '';
      border-top: 1px solid var(--color-font);
      position: relative;
      display: block;
      align-self: center;
      min-width: 0;
      width: auto;

      transition: min-width 0.3s ease-out;
      will-change: min-width;
    }
    &--visible {
      &::before {
        min-width: $grid-base;
      }
    }

    .arrow {
      position: relative;
      width: 0.7em;
      height: 0.7em;
    }
  }
}
.theme-teaser {
  overflow: hidden;
  height: 540px;
  background: linear-gradient(219.01deg, var(--color-grey-200) 0%, var(--color-grey-100) 100%);
  cursor: pointer;

  &__manufacturer-logo {
    margin-top: 3 * $grid-base;
    margin-left: 2 * $grid-base;
    height: 18px;
    display: inline-block;
  }
  &__manufacturer-heading {
    padding: 0 2 * $grid-base;
  }
}

.mouseover-teaser--rounded-white {
  border-radius: 5%;
  background: linear-gradient(219.01deg, var(--color-grey-200) 0%, var(--color-grey-100) 50%);
}

.mouseover-teaser--rounded-rotate-bg {
  border-radius: 5%;
  :deep(.mouseover-teaser__background) {
    transform: scale(1.1) rotate(5deg);
  }
}
</style>
